import { debounce } from '@repo/utils/debounce'
import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'
import { useToastStore } from '../useToastStore/useToastStore'

// Synthetic identity for a locally-picked File, since raw File objects have no id of their own.
const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`

const getNextSectionDisplayNumber = (sections) => {
  const sectionNamePattern = /^Section (\d+)$/
  const matchingSectionCount = sections.filter((section) => sectionNamePattern.test(section.name)).length
  const usedNumbers = new Set(
    sections
      .map((section) => Number(sectionNamePattern.exec(section.name)?.[1]))
      .filter((number) => !Number.isNaN(number))
  )

  let candidate = matchingSectionCount + 1
  while (usedNumbers.has(candidate)) {
    candidate += 1
  }

  return candidate
}

export const extractDownloadBundleErrorMessage = async (err) => {
  try {
    const data = err?.response?.data
    const text = data instanceof Blob ? await data.text() : data
    const outer = typeof text === 'string' ? JSON.parse(text) : text

    const outerMessage = outer?.message
    if (!outerMessage) return null

    const startIndex = outerMessage.indexOf('{')
    if (startIndex === -1) return outerMessage

    const inner = JSON.parse(outerMessage.substring(startIndex))
    return inner?.errors?.[0]?.message ?? outerMessage
  } catch {
    return null
  }
}

const initialState = {
  mode: 'create', // 'create' | 'edit'
  isAuthenticated: false,
  selectedMatter: null,
  matterBundleId: null,
  bundledocsPartitionKey: null,
  bundledocsRowKey: null,
  bundleName: '',
  description: '',
  sections: [],
  nextSectionId: 1,
  nextSectionDisplayNumber: 1,
  selectedDocuments: [],
  originalBundleData: null
}

export const useDocumentBundleWizardStore = storeWrapper('useDocumentBundleWizardStore', (set, get) => ({
  ...initialState,

  updateState: ({ key, value }) => {
    set({ [key]: value })
  },

  initialize: ({ mode = 'create', selectedMatter = null, bundleData = null } = {}) => {
    const sections = bundleData?.sections ?? []
    const nextSectionId = sections.reduce((maxId, section) => Math.max(maxId, section.id), 0) + 1

    set({
      ...initialState,

      mode,
      isAuthenticated: false,
      selectedMatter,
      matterBundleId: bundleData?.matterBundleId ?? null,
      bundledocsPartitionKey: bundleData?.bundledocsPartitionKey ?? null,
      bundledocsRowKey: bundleData?.bundledocsRowKey ?? null,
      bundleName: bundleData?.name ?? '',
      description: bundleData?.description ?? '',
      sections,
      nextSectionId,
      nextSectionDisplayNumber: getNextSectionDisplayNumber(sections),
      selectedDocuments: bundleData?.selectedDocuments ?? [],
      originalBundleData: bundleData
    })

    if (mode === 'edit' && bundleData?.matterBundleId) {
      get().fetchBundleSections({ matterBundleId: bundleData.matterBundleId })
    }
  },

  // Populates `sections` for Edit mode from the bundle's current section list so the
  // dynamic step-per-section logic in CreateDocumentBundles renders them exactly like Create Bundle.
  fetchBundleSections: async ({ matterBundleId }) => {
    try {
      const bundle = await ospreyAPI({
        method: 'GET',
        service: 'documents',
        endpoint: `Bundledocs/GetBundle/${matterBundleId}`
      })

      const sections = (bundle?.sections ?? []).map(({ matterBundleSection, documents }, index) => ({
        id: matterBundleSection?.matterBundleSectionId,
        name: matterBundleSection?.description ?? '',
        order: index,
        matterBundleSectionId: matterBundleSection?.matterBundleSectionId,
        bundledocsRowKey: matterBundleSection?.bundledocsRowKey,
        documents: documents ?? [],
        newAddedDocuments: []
      }))

      const nextSectionId = sections.reduce((maxId, section) => Math.max(maxId, section.id ?? 0), 0) + 1

      
      const bundleDetails = bundle?.matterBundle

      set({
        sections,
        nextSectionId,
        nextSectionDisplayNumber: getNextSectionDisplayNumber(sections),
        ...(bundleDetails?.description != null && { description: bundleDetails.description })
      })
    } catch (err) {
      console.error('Error fetching bundle sections:', err)
    }
  },

  addSection: () =>
    set((state) => {
      const nextId = state.nextSectionId

      return {
        sections: [
          ...state.sections,
          {
            id: nextId,
            name: `Section ${state.nextSectionDisplayNumber}`,
            order: nextId
          }
        ],
        nextSectionId: nextId + 1,
        nextSectionDisplayNumber: state.nextSectionDisplayNumber + 1
      }
    }),
  updateSectionName: (id, name) => {
    const trimmedName = name.trim()
    const isDuplicate = get().sections.some(
      (section) => section.id !== id && section.name.trim().toLowerCase() === trimmedName.toLowerCase()
    )

    if (isDuplicate) return false

    set((state) => ({
      sections: state.sections.map((section) => (section.id === id ? { ...section, name } : section))
    }))

    return true
  },
  moveSectionUp: (index) => {
    set((state) => {
      if (index === 0) {
        return state
      }

      const sections = [...state.sections]

      ;[sections[index - 1], sections[index]] = [sections[index], sections[index - 1]]

      return { sections }
    })
  },
  moveSectionDown: (index) => {
    set((state) => {
      if (index === state.sections.length - 1) {
        return state
      }

      const sections = [...state.sections]

      ;[sections[index], sections[index + 1]] = [sections[index + 1], sections[index]]

      return { sections }
    })
  },
  // BundleDocs requires a bundle to always retain at least one section, but this only matters once
  clearSections: () => {
    const { sections, mode } = get()

    if (mode !== 'edit') {
      set({ sections: [] })
      return
    }

    if (sections.length <= 1) {
      useToastStore
        .getState()
        .showToast({ type: 'error', title: 'Error', text: 'A bundle must contain at least one section.' })
      return
    }

    set({ sections: sections.slice(0, 1) })

    useToastStore.getState().showToast({
      type: 'info',
      title: 'Section retained',
      text: 'One section has been retained because a bundle must contain at least one section.'
    })
  },
  clearIndexedSection: (index) => {
    const { sections, mode } = get()

    if (mode === 'edit' && sections.length <= 1) {
      useToastStore
        .getState()
        .showToast({ type: 'error', title: 'Error', text: 'A bundle must contain at least one section.' })
      return
    }

    set((state) => ({
      sections: [...state.sections.slice(0, index), ...state.sections.slice(index + 1)]
    }))
  },
  addNewDocumentsToSection: ({ sectionId, files }) => {
    set((state) => ({
      sections: state.sections.map((section) =>
        section.id === sectionId
          ? { ...section, newAddedDocuments: [...(section.newAddedDocuments ?? []), ...files] }
          : section
      )
    }))
  },

  removeNewDocumentFromSection: ({ sectionId, fileKey }) => {
    const keepFile = (file) => getFileKey(file) !== fileKey
    const updateSection = (section) =>
      section.id === sectionId
        ? { ...section, newAddedDocuments: (section.newAddedDocuments ?? []).filter(keepFile) }
        : section

    set((state) => ({
      sections: state.sections.map(updateSection)
    }))
  },

  removeUploadedDocumentFromSection: ({ sectionId, documentName }) => {
    const keepDocument = (document) => document.name !== documentName
    const updateSection = (section) =>
      section.id === sectionId
        ? { ...section, uploadedDocuments: (section.uploadedDocuments ?? []).filter(keepDocument) }
        : section

    set((state) => ({
      sections: state.sections.map(updateSection)
    }))
  },
  fetchUploadedDocumentId: async ({ matterBundleSectionId, documentName }) => {
    const { matterBundleId } = get()

    try {
      const bundle = await ospreyAPI({
        method: 'GET',
        service: 'documents',
        endpoint: `Bundledocs/GetBundle/${matterBundleId}`
      })

      const matchingSection = (bundle?.sections ?? []).find(
        ({ matterBundleSection }) => matterBundleSection?.matterBundleSectionId === matterBundleSectionId
      )

      const matchingDocument = (matchingSection?.documents ?? []).find((document) => document.name === documentName)

      return matchingDocument?.matterBundleDocumentId ?? null
    } catch (err) {
      console.error('Error resolving uploaded document id:', err)
      return null
    }
  },

  createBundle: async ({ suppressErrorToast = false } = {}) => {
    const { selectedMatter, bundleName, description } = get()

    const matterBundle = await ospreyAPI({
      method: 'POST',
      service: 'documents',
      endpoint: 'Bundledocs/CreateBundle',
      suppressErrorToast,
      data: {
        clientNo: selectedMatter?.clientNo,
        matterNo: selectedMatter?.matterNo,
        name: bundleName,
        description
      }
    })

    set({
      matterBundleId: matterBundle?.matterBundleId ?? null,
      bundledocsPartitionKey: matterBundle?.bundledocsPartitionKey ?? null,
      bundledocsRowKey: matterBundle?.bundledocsRowKey ?? null
    })

    return matterBundle
  },

  updateBundle: async ({ suppressErrorToast = false } = {}) => {
    const { matterBundleId, description, sections } = get()

    const existingSections = sections.filter((section) => section.matterBundleSectionId)
    const newSections = sections.filter((section) => !section.matterBundleSectionId)

    try {
      await ospreyAPI({
        method: 'PUT',
        service: 'documents',
        endpoint: 'Bundledocs/EditBundle',
        suppressErrorToast: true,
        data: {
          matterBundleId,
          description,
          sections: existingSections.map((section) => ({
            matterBundleSectionId: section.matterBundleSectionId,
            description: section.name
          }))
        }
      })
    } catch (err) {
      const message = await extractDownloadBundleErrorMessage(err)
      if (message) err.message = message
      throw err
    }

    if (newSections.length) {
      await get().createSections({ sections: newSections })
    }

    const sectionsForReorder = get().sections.filter((section) => section.matterBundleSectionId)

    if (sectionsForReorder.length) {
      await ospreyAPI({
        method: 'PUT',
        service: 'documents',
        endpoint: `Bundledocs/ReorderSections/${matterBundleId}`,
        suppressErrorToast,
        data: sectionsForReorder.map((section, index) => ({
          rowKey: section.bundledocsRowKey,
          displayOrder: index + 1
        }))
      })
    }
  },

  createSections: async ({ sections: sectionsToCreate } = {}) => {
    const { matterBundleId, sections } = get()

    for (const section of sectionsToCreate ?? sections) {
      const createdSection = await ospreyAPI({
        method: 'POST',
        service: 'documents',
        endpoint: `Bundledocs/CreateSection/${matterBundleId}`,
        data: JSON.stringify(section.name)
      })

      const enrichSection = (existingSection) =>
        existingSection.id === section.id
          ? {
              ...existingSection,
              matterBundleSectionId: createdSection?.matterBundleSectionId ?? existingSection.matterBundleSectionId,
              bundledocsRowKey: createdSection?.bundledocsRowKey ?? existingSection.bundledocsRowKey,
              displayOrder: createdSection?.displayOrder ?? existingSection.displayOrder
            }
          : existingSection

      set((state) => ({ sections: state.sections.map(enrichSection) }))
    }
  },

  addDocumentsToSection: async ({ sectionId, sectionRowKey, files, suppressErrorToast = false } = {}) => {
    const { matterBundleId } = get()

    const form = new FormData()
    files.forEach((file) => form.append('files', file))

    await ospreyAPI({
      headers: { 'Content-Type': 'multipart/form-data; charset=utf-8' },
      method: 'POST',
      service: 'documents',
      endpoint: `Bundledocs/AddDocuments/${matterBundleId}/${sectionRowKey}`,
      data: form,
      suppressErrorToast
    })

    const uploadedDocuments = files.map((file) => ({ name: file.name, type: file.type, date: new Date() }))

    set((state) => ({
      sections: state.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              newAddedDocuments: [],
              uploadedDocuments: [...(section.uploadedDocuments ?? []), ...uploadedDocuments]
            }
          : section
      )
    }))
  },

  deleteDocument: async ({ matterBundleDocumentId, suppressErrorToast = false } = {}) => {
    await ospreyAPI({
      method: 'DELETE',
      service: 'documents',
      endpoint: `Bundledocs/DeleteDocument/${matterBundleDocumentId}`,
      suppressErrorToast
    })

    const removeDeletedDocument = (section) => ({
      ...section,
      documents: (section.documents ?? []).filter(
        (document) => document.matterBundleDocumentId !== matterBundleDocumentId
      )
    })

    set((state) => ({ sections: state.sections.map(removeDeletedDocument) }))
  },

  downloadBundle: async ({ matterBundleId, suppressErrorToast = false } = {}) => {
    try {
      return await ospreyAPI({
        method: 'GET',
        service: 'documents',
        endpoint: `Bundledocs/DownloadBundle/${matterBundleId}`,
        suppressErrorToast: true,
        responseType: 'blob'
      })
    } catch (err) {
      if (!suppressErrorToast) {
        const message = await extractDownloadBundleErrorMessage(err)
        useToastStore
          .getState()
          .showToast({ type: 'error', title: 'Error', text: message || 'Error downloading bundle' })
      }
      throw err
    }
  },

  addBundleToMatterHistory: async ({ matterBundleId, suppressErrorToast = false } = {}) => {
    try {
      return await ospreyAPI({
        method: 'POST',
        service: 'documents',
        endpoint: `Bundledocs/AddBundleToMatterHistory/${matterBundleId}`,
        suppressErrorToast: true,
        successToast: {
          type: 'success',
          title: 'Success',
          text: 'Bundle added to matter history'
        }
      })
    } catch (error) {
      if (!suppressErrorToast) {
        const message = await extractDownloadBundleErrorMessage(error)
        useToastStore.getState().showToast({ type: 'error', title: 'Error', text: message || 'Error to upload bundle' })
      }
      throw error
    }
  },

  fetchMatterAttachments: async ({ clientMatterNo, searchTerm, startAtIndex = 0, pageSize = 25 } = {}) => {
    if (clientMatterNo == null) return { rows: [], totalRowCount: 0 }

    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

    if (clientNo == null || matterNo == null) return { rows: [], totalRowCount: 0 }

    return new Promise((resolve) => {
      debounce({
        id: `fetchMatterAttachments-${clientMatterNo}`,
        delay: 300,
        callback: async () => {
          try {
            const attachments = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: '/MatterHistoryContent/GetAllBySearchTerms',
              data: {
                ClientNo: clientNo,
                MatterNo: matterNo,
                Description: searchTerm ?? null,
                StartAtIndex: startAtIndex,
                PageSize: pageSize
              }
            })

            resolve(attachments)
          } catch (err) {
            console.error('Error fetching matter attachments:', err)
            resolve({ rows: [], totalRowCount: 0 })
          }
        }
      })
    })
  },

  fetchAttachmentContent: async ({ matterHistoryContentId } = {}) => {
    if (!matterHistoryContentId) return null

    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAttachmentContent-${matterHistoryContentId}`,
        delay: 300,
        callback: async () => {
          try {
            const file = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: '/MatterHistoryContent/GetContentAsFile',
              data: { matterHistoryContentId, isConvertWordToPdf: false },
              timeout: 120000,
              responseType: 'blob',
              headers: { Accept: '*/*', 'Content-Type': 'application/json' }
            })

            resolve(file)
          } catch (err) {
            console.error('Error fetching attachment content:', err)
            reject(err)
          }
        }
      })
    })
  },

  reset: () => set(initialState)
}))
